import {z} from 'zod';
import {
	editEffectsSortOrder,
	type EditEffectsSortOrderParams,
	getSortField,
	type GetSortFieldParams,
	getSortOrder,
	type GetSortOrderParams,
	SortOrder,
	SortSchema,
} from '@netx/core-schema';
import {AssetViewFields, type IAssetView} from './assetViewsSchema';

export const ASSET_VIEWS_SORTABLE_FIELDS = [
	AssetViewFields.name,
	AssetViewFields.description,
	AssetViewFields.fileSize,
	AssetViewFields.creationDate,
	AssetViewFields.fileTypeLabel,
] as const;

export const AssetViewsSortSchema = SortSchema.omit({
	field: true,
}).extend({
	field: z.enum(ASSET_VIEWS_SORTABLE_FIELDS),
});

export type AssetViewsSort = z.infer<typeof AssetViewsSortSchema>;

export const AssetViewsSortableField = z.enum(ASSET_VIEWS_SORTABLE_FIELDS).enum;

export const ASSET_VIEWS_DEFAULT_SORT_FIELD = AssetViewsSortableField.name;

export const ASSET_VIEWS_DEFAULT_SORT_ORDER = SortOrder.ASCENDING;

/** will return the sort passed in if valid/specified, otherwise will return the defaults for the invalid/unspecified */
export const getAssetViewsDefaultSort = (
	sort: Partial<AssetViewsSort> = {},
): AssetViewsSort => ({
	field: getAssetViewsSortField({sortField: sort.field}),
	order: getAssetViewsSortOrder({sortOrder: sort.order}),
});

/** will return the passed in sort field if valid, otherwise will retutn the default sort field */
export const getAssetViewsSortField = (
	params: Omit<
		GetSortFieldParams<(typeof ASSET_VIEWS_SORTABLE_FIELDS)[number]>,
		'defaultSortField' | 'sortableFields'
	>,
) =>
	getSortField({
		...params,
		sortableFields: ASSET_VIEWS_SORTABLE_FIELDS,
		defaultSortField: ASSET_VIEWS_DEFAULT_SORT_FIELD,
	});

/** will return the passed in sort order if valid, otherwise will return the default sort order */
export const getAssetViewsSortOrder = (
	params: Omit<GetSortOrderParams, 'defaultSortOrder'>,
) =>
	getSortOrder({
		...params,
		defaultSortOrder: ASSET_VIEWS_DEFAULT_SORT_ORDER,
	});

/** given the original data, the new data, and the current sort, determine if the sort order was effected */
export const groupEditEffectsOrder = (
	params: Omit<
		EditEffectsSortOrderParams<
			IAssetView,
			(typeof ASSET_VIEWS_SORTABLE_FIELDS)[number]
		>,
		'sortableFields'
	>,
) =>
	editEffectsSortOrder({
		...params,
		sortableFields: ASSET_VIEWS_SORTABLE_FIELDS,
	});
